// One closing the assistant showed, drawn under its reply (B-112; since B-125 from the reply's
// `displays`, not from the text): an optional note (`nota`), then what the panel would say — office and region, date, state,
// its open differences, its resumen figures and what it is still waiting on — and two ways out,
// "Ver día" (that day's panel) and "Ver cierre" (the same CierreDetailDialog the other screens open,
// handed the panel row this card already read).
//
// Three requests per card, all reads: `getCierreClosingDetail` (state, alerts, office, date, client),
// that closing's panel row (`filterOffices` for its one date and office: region and the resumen
// figures) and the client's fields (`getExtractionFields`, which of those figures are the resumen —
// asked once per client and shared by every card). The state is the detail's own rule
// (`estadoDeDetalle`), so the card and the dialog never disagree. The card is a skeleton of its final
// size until every answer is in, so it does not grow twice while loading; if the detail can't be
// loaded it says so and keeps "Ver cierre", so a citation never disappears with a failed lookup.
import { useEffect, useState } from "react";
import { AlertTriangle, CalendarDays } from "lucide-react";

import { filterOffices, getCierreClosingDetail, getExtractionFields } from "../../../ApiCalls/mantisBusiness";
import {
  alertasVigentes,
  columnasDeResumen,
  esAlertaAbierta,
  estadoDeDetalle,
  etiquetaDeFaltantes,
  fechaDMY,
  numeroDeCelda,
  ordenarAlertasPorSeveridad,
  pendientesDeAlertas,
  valorLegible,
} from "../../../utils/cierrePanel";
import { EstadoBadge } from "./EstadoBadge";

// The client's fields, one request per business and client however many cards ask.
const camposPorCliente = new Map();
function camposDelCliente(token, mantisBusinessId, clientCode) {
  const clave = `${mantisBusinessId}|${clientCode}`;
  if (!camposPorCliente.has(clave)) {
    const pedido = getExtractionFields(token, mantisBusinessId, clientCode)
      .then((r) => r?.fields || r?.data?.fields || null)
      .catch(() => null);
    pedido.then((campos) => !campos && camposPorCliente.delete(clave));
    camposPorCliente.set(clave, pedido);
  }
  return camposPorCliente.get(clave);
}

// Its open differences, most serious first. A value still to arrive (`blocked`) is said apart, under
// "Falta", and good news (`info`) is neither.
export function diferenciasAbiertas(alertas) {
  return ordenarAlertasPorSeveridad(alertasVigentes(alertas || []).filter(esAlertaAbierta)).filter(
    (a) => a.severity !== "info" && a.severity !== "blocked"
  );
}

// A resumen field's label without the Matriz column it maps to: "Diferencia caja (AA)" is how the
// panel's wide table names the column; in the chat the letter means nothing.
export const etiquetaSinColumna = (etiqueta) => String(etiqueta || "").replace(/\s*\([A-Z]{1,3}\)\s*$/, "");

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// Static on purpose: a global stylesheet redefines the `pulse` keyframes with a green glow, which is
// what Tailwind's `animate-pulse` would pick up here.
const Barra = ({ className }) => <span className={`block rounded bg-brinks-surface ${className}`} />;

export function CierreCitadoCard({ closingId, nota, token, mantisBusinessId, onVer, onVerDia }) {
  const [detalle, setDetalle] = useState(null);
  const [fila, setFila] = useState(null);
  const [columnas, setColumnas] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let activo = true;
    (async () => {
      const res = await getCierreClosingDetail(token, mantisBusinessId, closingId).catch(() => null);
      if (!activo) return;
      const data = res?.success ? res.data : null;
      const { businessDate, officeCode, clientCode } = data?.closing || {};
      if (businessDate) {
        const filtros = { startDate: businessDate, endDate: businessDate, office: officeCode || undefined };
        const [filas, campos] = await Promise.all([
          filterOffices(token, filtros, 100, mantisBusinessId, "brinks", clientCode).catch(() => null),
          camposDelCliente(token, mantisBusinessId, clientCode),
        ]);
        if (!activo) return;
        setFila((filas?.success ? filas.data || [] : []).find((row) => row.closing_id === closingId) || null);
        setColumnas(columnasDeResumen(campos));
      }
      setDetalle(data);
      setCargando(false);
    })();
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, closingId]);

  const { officeName, officeCode, businessDate, clientCode } = detalle?.closing || {};
  const oficina = officeName && officeCode ? `${officeName} (${officeCode})` : officeName || officeCode;
  const estado = detalle ? estadoDeDetalle(detalle) : null;
  const abiertas = diferenciasAbiertas(detalle?.alerts);
  const pendientes = detalle ? pendientesDeAlertas(detalle.alerts || []) : null;
  const cifras = fila ? columnas.filter((c) => numeroDeCelda(fila[c.fieldName]) !== null) : [];
  const diaCorto = businessDate ? fechaDMY(businessDate).slice(0, 5) : "";
  const deQue = oficina ? ` de ${oficina}` : "";

  // Compact enough for two side by side (the chat lays consecutive cards out in two columns): long
  // texts wrap to two lines before they are cut, and the buttons sit at the foot so cards of one row
  // line up whatever each one has to say.
  return (
    <div className="flex h-full min-w-0 flex-col rounded-[12px] border border-brinks-border bg-card p-3">
      {cargando ? (
        <div aria-busy="true" aria-label="Cargando cierre" className="space-y-2">
          <Barra className="h-4 w-3/4" />
          <Barra className="h-3 w-1/2" />
          {nota ? <p className="line-clamp-2 text-[13px] leading-snug text-brinks-foreground">{nota}</p> : null}
          <Barra className="h-3 w-1/3" />
          <Barra className="h-12 w-full" />
        </div>
      ) : !detalle ? (
        <>
          <p className="text-[13px] font-semibold text-brinks-foreground">No se pudo cargar este cierre.</p>
          {nota ? <p className="mt-2 line-clamp-2 text-[13px] leading-snug text-brinks-foreground">{nota}</p> : null}
        </>
      ) : (
        <>
          <p className="line-clamp-2 text-[13px] font-semibold leading-snug text-brinks-foreground">{oficina || "Cierre"}</p>
          <div className="mt-1 flex items-center justify-between gap-2">
            <p className="min-w-0 truncate text-[11.5px] text-brinks-muted-foreground">
              {[fechaDMY(businessDate), fila?.region ? `Regional ${fila.region}` : null].filter(Boolean).join(" · ")}
            </p>
            {estado ? <EstadoBadge estado={estado} className="shrink-0 px-2 py-0.5 text-[11px]" /> : null}
          </div>

          {nota ? <p className="mt-2 line-clamp-2 text-[13px] leading-snug text-brinks-foreground">{nota}</p> : null}

          {/* The figures already say how much; with them on the card the alert only adds how many
              differences are open. Without them, the most serious one's own sentence does. */}
          {abiertas.length ? (
            <p className="mt-2 flex min-w-0 items-start gap-1.5 text-[12px] text-brinks-danger">
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
              {cifras.length ? (
                <span>{plural(abiertas.length, "alerta abierta", "alertas abiertas")}</span>
              ) : (
                <span className="line-clamp-2 min-w-0">
                  {abiertas[0].message}
                  {abiertas.length > 1 ? (
                    <span className="text-brinks-muted-foreground">
                      {` · +${plural(abiertas.length - 1, "alerta", "alertas")}`}
                    </span>
                  ) : null}
                </span>
              )}
            </p>
          ) : null}

          {cifras.length ? (
            <dl className="mt-2 space-y-0.5 rounded-[8px] bg-brinks-surface px-2.5 py-1.5">
              {cifras.map((c) => {
                const valor = numeroDeCelda(fila[c.fieldName]);
                return (
                  <div key={c.fieldName} className="flex items-baseline justify-between gap-2 text-[12px]">
                    <dt className="min-w-0 text-brinks-muted-foreground">{etiquetaSinColumna(c.etiqueta)}</dt>
                    <dd
                      className={`shrink-0 font-semibold tabular-nums ${
                        c.formato !== "count" && valor !== 0 ? "text-brinks-danger" : "text-brinks-foreground"
                      }`}
                    >
                      {valorLegible(valor, c.formato)}
                    </dd>
                  </div>
                );
              })}
            </dl>
          ) : null}

          {pendientes?.faltantes ? (
            <p className="mt-2 line-clamp-2 text-[12px] text-brinks-warning">{etiquetaDeFaltantes(pendientes.faltan)}</p>
          ) : null}
        </>
      )}

      <div className="mt-auto flex gap-2 pt-3">
        {cargando ? <Barra className="h-[30px] flex-1 rounded-[8px]" /> : null}
        {businessDate ? (
          <button
            type="button"
            onClick={() => onVerDia({ businessDate, clientCode })}
            aria-label={`Ver el día ${fechaDMY(businessDate)} en el panel${deQue ? `, cierre${deQue}` : ""}`}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-[8px] border border-brinks-border bg-background px-2.5 py-1.5 text-[12px] font-medium text-brinks-foreground transition hover:bg-brinks-surface-hover"
          >
            <CalendarDays className="h-3.5 w-3.5" />
            {`Ver día ${diaCorto}`}
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => onVer(closingId, fila)}
          aria-label={`Ver cierre${deQue}`}
          className="flex-1 rounded-[8px] bg-primary px-2.5 py-1.5 text-[12px] font-medium text-primary-foreground transition hover:opacity-90"
        >
          Ver cierre
        </button>
      </div>
    </div>
  );
}

export default CierreCitadoCard;
